const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};
// export interface LocalizedComponent {
//   $t(t: string): string;
//   $d(d: Date | string, p?: Intl.DateTimeFormatOptions): string;
//   $n(n: number, p?: Intl.NumberFormatOptions): string;
//   $localize: LocalizeController;
// }

export { LocalizeController, LocalizeMixin, registerTranslation };
//# sourceMappingURL=index.js.map
